import { useState } from 'react';

import { OrderSummary, type OrderSummaryItem } from '@/features/tickets';
import { useI18n } from '@/providers/i18n';
import { useCart, useCartCount, useCartSubtotal } from '@/store/cart';

import CartEmpty from './components/CartEmpty';
import CartRow from './components/CartRow';
import CheckoutDetails from './components/CheckoutDetails';
import CheckoutPayment from './components/CheckoutPayment';
import CheckoutSuccess from './components/CheckoutSuccess';
import StepHeader from './components/StepHeader';
import styles from './CheckoutPage.module.css';

type Step = 1 | 2 | 3 | 4;

export default function CheckoutPage() {
  const { t } = useI18n();
  const cart = useCart();
  const count = useCartCount();
  const subtotal = useCartSubtotal();
  const [step, setStep] = useState<Step>(1);

  const fee = Math.round(subtotal * 0.07);
  const total = subtotal + fee;

  if (cart.items.length === 0 && step < 4) {
    return <CartEmpty />;
  }

  if (step === 4) {
    return <CheckoutSuccess />;
  }

  const orderRef = `#${Math.abs(count * 7919).toString(36).toUpperCase()}`;
  const summaryItems: OrderSummaryItem[] = cart.items.map((it) => ({
    key: it.key,
    label: it.name,
    qty: it.qty,
  }));

  return (
    <section>
      <header className="page-h">
        <div>
          <span className="nano">/ CHECKOUT / {String(step).padStart(2, '0')} OF 03</span>
          <h1>{t('cta.checkout')}</h1>
        </div>
        <span className="meta">{count} ITEM(S)</span>
      </header>

      <div className={styles.layout}>
        <div>
          {step === 1 && (
            <>
              <StepHeader step={1} label="REVIEW CART" />
              {cart.items.map((it) => (
                <CartRow
                  key={it.key}
                  item={it}
                  onQtyChange={(n) => cart.setQty(it.key, n)}
                  onRemove={() => cart.remove(it.key)}
                />
              ))}
              <button
                type="button"
                className={`btn btn-fill btn-arrow ${styles.continue}`}
                onClick={() => setStep(2)}
              >
                {t('cta.continue')}
              </button>
            </>
          )}

          {step === 2 && (
            <CheckoutDetails onBack={() => setStep(1)} onSubmit={() => setStep(3)} />
          )}

          {step === 3 && (
            <CheckoutPayment
              onBack={() => setStep(2)}
              onSubmit={() => {
                cart.clear();
                setStep(4);
              }}
            />
          )}
        </div>

        <OrderSummary
          orderRef={orderRef}
          items={summaryItems}
          subtotal={subtotal}
          fee={fee}
          total={total}
        />
      </div>
    </section>
  );
}
